import { useState } from "react";
import { PageHeader } from "../../../components/PortfolioPerformance/page-header";
import { Panel } from "../../../components/PortfolioPerformance/panel";
import { Skeleton } from "../../../ui/skeleton";
import { BasisToggle, type ThreeWayBasis } from "../../../components/PortfolioPerformance/basis-toggle";
import { IndustrySelect, YearChips } from "../../../components/PortfolioPerformance/chart-filters";
import { TrendLineChart } from "../../../components/PortfolioPerformance/charts/trend-line-chart";
import {
  useChargeoffStats,
  useChargeoffCurves,
} from "../lib/api/chargeoff-acceleration";
import { useAssetIndustries } from "../lib/api/asset-industries";
import { useBorrowerIndustries } from "../lib/api/borrower-industries";
import { cohortColor } from "../lib/api/lifetime-loss-projections";

const ALL_ASSET_INDUSTRIES_LABEL = "All Industry";
const ALL_BORROWER_INDUSTRIES_LABEL = "All Borrower Industries";

export default function ChargeoffAccelerationPage() {
  // Realized / Projected / Active picks the Net Loss and Default Rate figures AND the funded years the curves offer (the years that
  // have rows on that side in the data), like the Vintage Analysis of Net Loss chart. The chips filter both curves; an empty selection
  // means every funded year on offer.
  const [basis, setBasis] = useState<ThreeWayBasis>("realized");
  const [selectedYears, setSelectedYears] = useState<string[]>([]);
  const [assetIndustry, setAssetIndustry] = useState<string | null>(null);
  const [borrowerIndustry, setBorrowerIndustry] = useState<string | null>(null);

  const borrowerIndustries = useBorrowerIndustries().data ?? [];
  const assetIndustries = useAssetIndustries().data ?? [];
  const stats = useChargeoffStats(basis);
  const curves = useChargeoffCurves(basis, assetIndustry ?? undefined, borrowerIndustry);

  // The years come straight from the portfolio curve: one column per funded year.
  const allYears = Array.from(
    new Set((curves.data?.portfolioCurve ?? []).flatMap((r) => Object.keys(r).filter((k) => k !== "monthsOnBook"))),
  ).sort();
  const activeSelected = selectedYears.filter((y) => allYears.includes(y));
  const visibleYears = activeSelected.length ? activeSelected : allYears;

  function toggleYear(year: string) {
    setSelectedYears((prev) => (prev.includes(year) ? prev.filter((y) => y !== year) : [...prev, year]));
  }

  const series = visibleYears.map((year) => ({
    dataKey: year,
    name: year,
    color: cohortColor(allYears.indexOf(year)),
  }));

  const basisToggle = (
    <BasisToggle
      value={basis}
      onChange={setBasis}
      realizedHint="Realized months of the funded years"
      projectedHint="Projected months of the funded years"
      activeHint="Funded years that still have active contracts"
    />
  );
  // The industry chart's header controls: its asset and borrower industry filters. The Realized / Projected / Active toggle
  // is not in a chart: it sits below the cards and drives both charts.
  const filterControls = (
  <div className="flex flex-nowrap items-center gap-2">
    <IndustrySelect
      value={assetIndustry ?? ALL_ASSET_INDUSTRIES_LABEL}
      options={[ALL_ASSET_INDUSTRIES_LABEL, ...assetIndustries]}
      onChange={(value) => setAssetIndustry(value === ALL_ASSET_INDUSTRIES_LABEL ? null : value)}
      ariaLabel="Asset industry"
      className="w-[170px]"
    />
    <IndustrySelect
      value={borrowerIndustry ?? ALL_BORROWER_INDUSTRIES_LABEL}
      options={[ALL_BORROWER_INDUSTRIES_LABEL, ...borrowerIndustries]}
      onChange={(value) =>
        setBorrowerIndustry(value === ALL_BORROWER_INDUSTRIES_LABEL ? null : value)
      }
      ariaLabel="Borrower industry"
      className="w-[170px]"
    />
  </div>
  );
  const chips = <YearChips years={allYears} selected={activeSelected} onToggle={toggleYear} />;

  function body(rows: { monthsOnBook: number; [cohort: string]: number }[] | undefined) {
    if (curves.isError) {
      return (
        <div
          role="alert"
          className="border border-decline/50 bg-decline/10 p-5 text-sm text-foreground"
        >
          <p className="font-semibold">Chargeoff curve data could not be read.</p>
          <p className="mt-1 text-muted-foreground">
            {curves.error instanceof Error ? curves.error.message : "Unknown failure."}
          </p>
        </div>
      );
    }
    if (!rows) return <Skeleton className="h-64 w-full" />;
    if (allYears.length === 0) {
      return <p className="text-sm text-muted-foreground">No chargeoff data for this ticket size yet.</p>;
    }
    return (
      <>
        {chips}
        <TrendLineChart
          data={rows.map((r) => ({ month: String(r.monthsOnBook), ...r }))}
          series={series}
          decimals={0}
          suffix=""
          height={260}
        />
      </>
    );
  }

  return (
    <div>
      <PageHeader
        title="Chargeoff Acceleration"
        subtitle="How fast the portfolio charges off, cumulative charged-off contracts vs. months on book: industry and portfolio level, side by side."
      />

      {/* The cards and the curves are separate queries, so each loads and errors on its own. */}
      <div className="mt-8 space-y-6">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {[
            { label: "Net Investment", value: stats.data?.netInvestment },
            { label: "Net Loss (Life to Date)", value: stats.data?.netLoss },
            { label: "Default Rate", value: stats.data?.defaultRate },
            { label: "Unearned Interest (as of today)", value: stats.data?.unearnedInterest },
            { label: "Earned Interest (YTD)", value: stats.data?.earnedInterest },
            { label: "Contract Chargeoff", value: stats.data?.contractChargeoffs },
          ].map((s) => (
            <div key={s.label} className="border border-border bg-card p-4">
              <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                {s.label}
              </p>
              <p className="mt-1 text-lg font-semibold tabular-nums text-foreground">
                {stats.isError ? "—" : stats.isPending ? "…" : s.value}
              </p>
            </div>
          ))}
        </div>

        {/* Realized / Projected / Active for both charts, top right, just below the cards. */}
        <div className="flex justify-end">{basisToggle}</div>

        {/* Side by side so the industry curve can be read against the whole portfolio's. */}
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <Panel
            title="Industry Chargeoff Acceleration"
            description={`Asset industry: ${assetIndustry ?? ALL_ASSET_INDUSTRIES_LABEL}. Cumulative charged-off contracts.`}
            right={filterControls}
            rightAlign="center"
          >
            {body(curves.data?.industryCurve)}
          </Panel>

          <Panel
            title="Portfolio Chargeoff Acceleration"
            description="All industries. Cumulative charged-off contracts."
          >
            {body(curves.data?.portfolioCurve)}
          </Panel>
        </div>

        <p className="text-xs text-muted-foreground">
          2020–2021 cohorts run visibly flatter: market-intervention years (moratoriums,
          restructuring). Curves are cumulative contract counts, not dollar value.
        </p>
      </div>
    </div>
  );
}
